import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const StoreContext = createContext(null);
export const useStore = () => useContext(StoreContext);

// Configure axios to send cookies for Emergent Google Auth
axios.defaults.withCredentials = true;

export function StoreProvider({ children }) {
  const [user, setUser] = useState(() => { const s = localStorage.getItem("eloire_user"); return s ? JSON.parse(s) : null; });
  const [token, setToken] = useState(() => localStorage.getItem("eloire_token") || null);
  const [cart, setCart] = useState(() => { const s = localStorage.getItem("eloire_cart"); return s ? JSON.parse(s) : []; });
  const [wishlist, setWishlist] = useState([]);  // array of product_ids
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);

  useEffect(() => { localStorage.setItem("eloire_cart", JSON.stringify(cart)); }, [cart]);

  useEffect(() => {
    axios.get(`${API}/products`).then(r => setProducts(r.data)).catch(() => {}).finally(() => setLoadingProducts(false));
  }, []);

  const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    // Fetch wishlist when user logs in
    if (user && token) {
      axios.get(`${API}/wishlist`, { headers: authHeaders }).then(r => setWishlist(r.data.map(p => p.id))).catch(() => {});
    } else {
      setWishlist([]);
    }
  }, [user, token]);

  const persistUser = (u, t) => {
    setUser(u); setToken(t);
    localStorage.setItem("eloire_user", JSON.stringify(u));
    localStorage.setItem("eloire_token", t);
  };

  const login = async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password });
    persistUser(data.user, data.token);
    toast.success(`Welcome back, ${data.user.name} ✨`);
    return data.user;
  };

  const signup = async (payload) => {
    const { data } = await axios.post(`${API}/auth/signup`, payload);
    persistUser(data.user, data.token);
    toast.success(`Welcome to Eloire, ${data.user.name} ✨`);
    return data.user;
  };

  const setGoogleAuth = (u, t) => {
    persistUser(u, t);
    toast.success(`Welcome, ${u.name} ✨`);
  };

  const logout = async () => {
    try { await axios.post(`${API}/auth/logout`); } catch {}
    setUser(null); setToken(null); setWishlist([]);
    localStorage.removeItem("eloire_user"); localStorage.removeItem("eloire_token");
    toast("Signed out");
  };

  const addToCart = (product, qty = 1) => {
    setCart(prev => {
      const existing = prev.find(p => p.product_id === product.id);
      if (existing) return prev.map(p => p.product_id === product.id ? { ...p, quantity: p.quantity + qty } : p);
      return [...prev, { product_id: product.id, name: product.name, price: product.price, quantity: qty, image: product.images?.[0], slug: product.slug }];
    });
    toast.success(`${product.name} added to bag`);
  };
  const updateQty = (product_id, quantity) => {
    if (quantity <= 0) return removeFromCart(product_id);
    setCart(prev => prev.map(p => p.product_id === product_id ? { ...p, quantity } : p));
  };
  const removeFromCart = (product_id) => setCart(prev => prev.filter(p => p.product_id !== product_id));
  const clearCart = () => setCart([]);

  const toggleWishlist = async (product) => {
    if (!user) { toast.error("Please sign in to save favourites"); return; }
    try {
      const { data } = await axios.post(`${API}/wishlist/toggle`, { product_id: product.id }, { headers: authHeaders });
      setWishlist(prev => data.added ? [...new Set([...prev, product.id])] : prev.filter(id => id !== product.id));
      toast.success(data.added ? `Saved ${product.name} to wishlist` : `Removed from wishlist`);
    } catch { toast.error("Couldn't update wishlist"); }
  };

  const inWishlist = (product_id) => wishlist.includes(product_id);

  const cartCount = cart.reduce((s, i) => s + i.quantity, 0);
  const cartSubtotal = cart.reduce((s, i) => s + i.price * i.quantity, 0);

  return (
    <StoreContext.Provider value={{
      user, token, authHeaders, login, signup, logout, setGoogleAuth,
      cart, addToCart, updateQty, removeFromCart, clearCart, cartCount, cartSubtotal,
      products, loadingProducts,
      wishlist, toggleWishlist, inWishlist,
    }}>
      {children}
    </StoreContext.Provider>
  );
}

/**
 * Add sizing params to remote image URLs so we ship right-sized bytes.
 * Unsplash: ?w=800&q=75&auto=format
 * Pexels:   ?auto=compress&cs=tinysrgb&w=800
 * Others: returned as-is
 */
export function optimizedImg(url, w = 800) {
  if (!url) return url;
  try {
    const u = new URL(url);
    if (u.hostname.includes("unsplash.com")) {
      u.searchParams.set("w", String(w));
      u.searchParams.set("q", "75");
      u.searchParams.set("auto", "format");
      u.searchParams.set("fit", "crop");
      return u.toString();
    }
    if (u.hostname.includes("pexels.com")) {
      u.searchParams.set("auto", "compress");
      u.searchParams.set("cs", "tinysrgb");
      u.searchParams.set("w", String(w));
      return u.toString();
    }
    return url;
  } catch { return url; }
}

export const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");
